import { useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Select } from "@/components/design/Select";
import { PersonLink } from "@/components/common/PersonLink";
import { EVENT_KIND_LABELS, type PlanPerson, type ProjectEvent, type ProjectPackage } from "./project-plan";
import {
  buildLanes,
  dayMs,
  monthTicks,
  percentFor,
  extendRange,
  scheduleDays,
  scheduleRange,
  stackMarkLabels,
  weekLabelEvery,
  weekTicks,
} from "./project-schedule";

// Personfokus: eine Spur je Person, Zeit von links nach rechts. Die Balken sind
// die Aufgaben aus den Paketen, platziert nach ihrem Datum.
//
// Zwei Regeln, die den Unterschied zwischen lesbar und Chaos machen:
// 1. Überlappende Aufgaben derselben Person liegen UNTEREINANDER, nie
//    übereinander - sonst verdeckt eine die andere und man sieht ausgerechnet
//    die Überschneidung nicht.
// 2. Die Zeitachse hat eine Mindestbreite in Pixeln pro Tag und scrollt
//    waagerecht. Ein halbes Jahr in eine Kartenbreite zu quetschen macht jeden
//    Balken zum Strich; und nichts darf aus der Karte herauslaufen. Die
//    Namensspalte steht dabei fest und trägt eine Haarlinie an der rechten
//    Kante: ohne sie sieht ein durchscrollender Balken aus, als läge er unter
//    dem Namen, statt an einer Spaltenkante zu enden.
//
// Die Einfärbung läuft über die Tintenrollen statt über Farben: vier Pakete
// brauchen vier unterscheidbare Balken, und eine eigene Palette wäre ein
// Systemwechsel (UI_CONVENTIONS "Changing this system"). Die beiden kräftigen
// Stufen sind die deckende Inverse-Fläche (Schrift darauf ist für jede Palette
// lesbar), die zweite unterscheidet sich durch eine Innenkante. Die beiden
// hellen Stufen sind Lasuren, auf denen die Tinte selbst lesbar bleibt.
//
// Die Signalfarbe des Mandanten bleibt hier bewusst AUSSEN VOR und ist allein
// den Terminen vorbehalten. Trägt die Arbeit dieselbe Farbe wie die Fristen,
// signalisiert nichts mehr etwas: eine Fläche voller Signal ist eine Fläche
// ohne Signal.
//
// Die Stufen liegen hoeher als frueher (85/60/40/25) und jeder Balken traegt
// eine Innenkante: die unteren Stufen waren blasse Floeze, die im Creme der
// Flaeche versanken. Ein Balken ist ein Gegenstand auf der Achse, kein Schatten -
// er braucht einen Rand, damit man sieht, wo er anfaengt und aufhoert. Dieselbe
// Regel wie beim Karriere-Graph, wo jeder Knoten eine 2px-Kontur hat.
const PACKAGE_TONES: readonly { bar: string; text: string; ring: string }[] = [
  { bar: "bg-ink ring-ink/20", text: "text-ink", ring: "ring-ink/60" },
  { bar: "bg-ink ring-2 ring-inset ring-ink-ui", text: "text-ink", ring: "ring-ink/45" },
  { bar: "bg-ink/50 ring-ink/25", text: "text-ink", ring: "ring-ink/35" },
  { bar: "bg-ink/32 ring-ink-ui", text: "text-ink", ring: "ring-ink/25" },
] as const;

/**
 * Ab welcher Pixelbreite die Beschriftung IN den Balken passt. Darunter steht sie
 * daneben: ein Balken von zwei Tagen traegt kein Wort, und ein abgeschnittener
 * erster Buchstabe ist schlechter als gar keiner. Die Breite laesst sich rechnen,
 * weil die Flaeche genau `scheduleDays * pxPerDay` breit ist.
 */
const LABEL_FITS_PX = 64;

// Etwas breiter, seit in der Spur auch das Bild der Person steht: mit Avatar
// blieben von einem Namen sonst zwei Silben übrig.
const NAME_COL = "w-36";

/**
 * Wie weit die Achse auseinandergezogen wird, in Pixeln pro Tag. Drei Stufen, weil
 * ein Plan drei Fragen hat: passt das Ganze aufs Bild, wie liegen die Monate
 * zueinander, was passiert in dieser Woche. Die mittlere ist der frühere
 * Festwert, ein Projekt sieht beim Öffnen also aus wie vorher.
 *
 * Gestaucht wird nur die BREITE, nie die Verhältnisse darauf: die Balken liegen
 * in Prozent der Gesamtspanne, ihre Lage zueinander bleibt in jeder Stufe gleich.
 */
// Skalierung als PIXEL JE KALENDERWOCHE. Das ist die Grösse, die der Mensch
// beim Zoomen meint: "Klein" heisst viele Wochen nebeneinander, "Gross" heisst
// eine breite Woche, in der die Tage auseinanderliegen.
//
// Zwei Umwege standen vorher hier. Feste Pixel je Tag als Mindestbreite taten
// bei kurzen Projekten nichts, weil die Fläche breiter war als die Untergrenze.
// Ein Vielfaches der Fläche wirkte zwar, änderte aber nur die Breite derselben
// zwei Kalenderwochen - gezoomt wurde die Zeichnung, nicht der Zeitraum.
const SCALES = { compact: 30, normal: 70, detail: 170 } as const;
/** Breite der klebenden Namensspalte (NAME_COL) in Pixeln - sie trägt keine Zeit. */
const NAME_COL_PX = 144;
/**
 * Kleinste sichtbare Balkenlänge. Ein Paket von einem Tag ist auf einer
 * Jahresachse ein Pünktchen von drei Pixeln - man hielt es für ein Kästchen
 * neben dem Namen und nicht für den Balken selbst. Die Lage bleibt exakt, nur
 * die Länge wird auf ein lesbares Mass gehoben.
 */
const MIN_BAR_PX = 10;
/** Höhe einer Beschriftungsstufe über der Achse. */
const MARK_ROW_PX = 20;
/** Wie breit die Beschriftung eines Termins höchstens wird (max-w-[12rem]). */
const MARK_LABEL_MAX_PX = 192;
/** Luft zwischen zwei Beschriftungen, plus die Raute selbst. */
const MARK_GAP_PX = 20;
type Scale = keyof typeof SCALES;

export function ProjectSwimlanes({
  packages,
  events = [],
  people,
  selectedTaskId = null,
  onSelectTask,
}: {
  packages: readonly ProjectPackage[];
  /** Termine liegen quer über allen Spuren - ein Datum gilt für alle zugleich. */
  events?: readonly ProjectEvent[];
  /** Die am Projekt Beteiligten - je Person eine Spur, in dieser Reihenfolge. */
  people: readonly PlanPerson[];
  /** Gesetzt, wenn ein Balken gerade bearbeitet wird - er bekommt dann einen Ring. */
  selectedTaskId?: string | null;
  /**
   * Fehlt im reinen Anzeigefall; dann sind die Balken nicht anklickbar.
   * Bekommt die Id eines Pakets ODER einer Aufgabe: beide sind Arbeit mit
   * Zeitraum und Zuständigkeit, und was man sieht, muss man auch anfassen
   * können. Vorher waren Pakete stumm, was sich wie ein Fehler las.
   */
  onSelectTask?: (barId: string) => void;
}) {
  const intl = useIntl();
  const [scale, setScale] = useState<Scale>("normal");
  // Zugeklappte Spuren. Als Menge der ZUGEKLAPPTEN, nicht der offenen: eine neu
  // hinzukommende Person ist dann von selbst sichtbar.
  const [collapsedLanes, setCollapsedLanes] = useState<readonly string[]>([]);
  // Die sichtbare Breite ist der Bezug der Skalierung, also muss sie gemessen
  // werden. Container-Queries können das nicht: sie schalten Klassen, geben
  // aber keine Zahl her, und die Beschriftungsdichte der Wochen rechnet mit
  // Pixeln je Tag.
  const viewportRef = useRef<HTMLDivElement>(null);
  const [viewportWidth, setViewportWidth] = useState(0);
  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;
    const measure = () => setViewportWidth(element.clientWidth);
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);
  const pxPerDay = SCALES[scale] / 7;
  // Was an Tagen in die freie Fläche passt - der Zeitraum, den diese Stufe zeigt.
  const axisViewport = Math.max(viewportWidth - NAME_COL_PX, 0);
  const capacityDays = Math.floor(axisViewport / pxPerDay);
  const projectRange = scheduleRange(packages, events);
  // Erst auffüllen, dann alles auf DIESEN Bereich rechnen: Achse, Wochen,
  // Termine und Balken. Vorher holte sich buildLanes seinen eigenen Bereich -
  // ohne Termine und ohne Zoomstufe - und die Balken standen auf einem anderen
  // Massstab als die Achse darüber.
  const range = projectRange && capacityDays > 0 ? extendRange(projectRange, capacityDays) : projectRange;
  const lanes = buildLanes(
    packages,
    people,
    intl.formatMessage({ id: "project.lanes.unassigned", defaultMessage: "Ohne Zuweisung" }),
    range,
  );
  const personById = new Map(people.map((p) => [p.id, p]));

  if (!range) {
    return (
      <p className="smycles-field-meta text-ink-muted">
        <FormattedMessage id="project.lanes.empty" defaultMessage="Noch keine Aufgabenpakete mit Zeitraum." />
      </p>
    );
  }

  const ticks = monthTicks(range);
  // Kalenderwochen als feinere Teilung unter den Monaten. Beschriftet wird nur
  // jede n-te, damit die Zahlen nicht aneinanderstossen; die uebrigen bleiben
  // Striche.
  //
  // Und der eigentliche Punkt: An jeder Wochenmarke haengt der Zeitraum im
  // Klartext. "KW 40" sagt den wenigsten Menschen etwas, "29.09. bis 05.10."
  // jedem - wer die Woche nicht im Kopf hat, faehrt hin und liest nach.
  const weeks = weekTicks(range);
  // Die Achse ist so breit, wie der Zeitraum bei dieser Stufe braucht. Passt er
  // in die Fläche, füllt er sie genau aus; ist das Projekt länger, wird
  // gescrollt - das ist der Sinn des Hineinzoomens.
  const axisWidth = scheduleDays(range) * pxPerDay;
  const totalWidth = axisWidth + NAME_COL_PX;
  const labelEvery = weekLabelEvery(pxPerDay);
  // Termine sind senkrechte Marken über alle Spuren. Sie gehören keiner Person,
  // sondern dem Projekt, also laufen sie quer durch statt in einer Zeile zu sitzen.
  const placed = events
    .map((event) => ({ event, ms: dayMs(event.date) }))
    .filter((m) => Number.isFinite(m.ms))
    .map((m) => ({ ...m, percent: percentFor(m.ms, range) }))
    .filter((m) => m.percent >= 0 && m.percent <= 100)
    .map((m) => ({
      ...m,
      // Nahe am rechten Rand kippt die Beschriftung auf die andere Seite der
      // Raute. Sonst schiebt der letzte Termin seinen Namen aus der Fläche, und
      // ausgerechnet der Abgabetermin ist der, den man nicht mehr lesen kann.
      labelLeft: m.percent > 75,
      // Geschätzt, nicht gemessen: eine Messung je Termin hiesse ein
      // Layout-Durchlauf je Termin, und für "stossen die aneinander" reicht die
      // Breite auf ein paar Pixel genau. 12px-Schrift, gedeckelt wie das Element.
      labelWidth: Math.min(MARK_LABEL_MAX_PX, m.event.title.length * 6.2 + MARK_GAP_PX),
    }));
  const markLevels = stackMarkLabels(placed, axisWidth);
  const marks = placed.map((m, i) => ({ ...m, level: markLevels[i] ?? 0 }));
  // Platz über der Achse für die höchste belegte Stufe.
  const markHeadroom = MARK_ROW_PX * (Math.max(0, ...marks.map((m) => m.level)) + 1);

  return (
    <div>
      {/* Die Weite gehört an die Achse, die sie verändert, nicht in die Kopfzeile
          der Seite: dort steht, WAS man sieht, hier nur, wie nah.
          Beschriftetes Auswahlfeld statt Umschalter: "Klein / Mittel / Gross"
          allein sagt nicht, WAS klein wird, und eine Kapsel mit drei Wörtern
          wiegt über einer leeren Zeitachse schwerer als das, was sie steuert.
          Dieselbe Form wie das Sortieren im Projektfokus. */}
      <label className="mb-3 inline-flex items-center gap-2">
        <span className="text-smycles-micro tracking-[0.2em] text-ink-muted">
          {intl.formatMessage({ id: "project.lanes.scaleLabel", defaultMessage: "Skalierung" })}
        </span>
        <Select
          variant="pill"
          hideEmpty
          value={scale}
          onChange={(next) => setScale(next as Scale)}
          options={[
            { value: "compact", label: intl.formatMessage({ id: "project.lanes.scaleCompact", defaultMessage: "Klein" }) },
            { value: "normal", label: intl.formatMessage({ id: "project.lanes.scaleNormal", defaultMessage: "Mittel" }) },
            { value: "detail", label: intl.formatMessage({ id: "project.lanes.scaleDetail", defaultMessage: "Groß" }) },
          ]}
        />
      </label>

      <div ref={viewportRef} className="overflow-x-auto">
        <div className="relative" style={{ minWidth: viewportWidth > 0 ? `${totalWidth}px` : "100%" }}>
          {/* Die Terminlinie liegt über ALLEM, von der Raute in der Kopfzeile bis
              unter die letzte Spur. Vorher begann sie erst an den Spuren, dann
              schwebte die Raute über einem Strich, der woanders anfing - dabei
              sind Marke und Linie ein Ding: der Zeitpunkt. */}
          <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-[var(--z-raised)] flex">
            <div className={`${NAME_COL} shrink-0`} />
            <div className="relative flex-1">
              {marks.map((m) => (
                <div
                  key={m.event.id}
                  className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-accent"
                  style={{ left: `${m.percent}%` }}
                />
              ))}
            </div>
          </div>
          <div className="flex" style={{ paddingTop: `${markHeadroom}px` }}>
            <div className={`${NAME_COL} sticky left-0 z-[var(--z-sticky)] shrink-0 border-r border-ink/10 bg-surface`} />
            {/* Zwei Ebenen mit eigener Hoehe: Monat oben, Woche unten. In einer
                gemeinsamen Zeile schoben sich "Jan 20" und die Wochenzahl
                uebereinander, sobald beide am selben Tag anfingen. */}
            <div className="relative h-9 flex-1">
              {weeks.map((w, i) => (
                <div
                  key={`w-${w.startMs}`}
                  className="absolute bottom-0 flex flex-col items-start"
                  style={{ left: `${w.percent}%` }}
                  title={intl.formatMessage(
                    { id: "project.plan.lanes.weekRange", defaultMessage: "KW {week}: {from} bis {to}" },
                    {
                      week: w.week,
                      from: intl.formatDate(w.startMs, { day: "2-digit", month: "2-digit", timeZone: "UTC" }),
                      to: intl.formatDate(w.endMs, { day: "2-digit", month: "2-digit", timeZone: "UTC" }),
                    },
                  )}
                >
                  {i % labelEvery === 0 ? (
                    <span className="whitespace-nowrap pl-1 text-smycles-micro leading-none text-ink-muted">{w.week}</span>
                  ) : null}
                  <span aria-hidden="true" className="h-1 w-px bg-ink/15" />
                </div>
              ))}
              {ticks.map((t) => (
                <div key={t.ms} className="absolute top-0" style={{ left: `${t.percent}%` }}>
                  <div className="smycles-field-meta whitespace-nowrap pl-1 leading-none text-ink-muted">
                    {intl.formatDate(t.ms, { month: "short", year: "2-digit", timeZone: "UTC" })}
                  </div>
                </div>
              ))}
              {marks.map((m) => (
                // Raute und Beschriftung sind ZWEI Elemente, nicht eines. Die
                // Raute steht unverrückbar auf ihrem Datum - sie ist die
                // Aussage der Zeichnung. Nur der Text weicht aus, wenn ein
                // Nachbar zu nah steht, und zwar nach oben auf seine Stufe.
                // Als gemeinsames Flex-Element wanderte vorher die Marke mit
                // und zeigte auf den falschen Tag.
                <div key={m.event.id}>
                  <span
                    aria-hidden="true"
                    className={`absolute h-2 w-2 -translate-x-1/2 rotate-45 ${
                      m.event.kind === "meeting" ? "bg-page ring-1 ring-accent-decoration/60" : "bg-accent"
                    }`}
                    style={{ left: `${m.percent}%`, top: `${-MARK_ROW_PX + 6}px` }}
                  />
                  <span
                    className={`smycles-field-meta absolute max-w-[12rem] truncate leading-tight text-ink ${
                      m.labelLeft ? "-translate-x-full pr-2 text-right" : "pl-2"
                    }`}
                    style={{ left: `${m.percent}%`, top: `${-(m.level + 1) * MARK_ROW_PX}px` }}
                    title={`${m.event.title} · ${intl.formatMessage(EVENT_KIND_LABELS[m.event.kind])}`}
                  >
                    {m.event.title}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Ein Termin ist EINE Linie durch den ganzen Plan, nicht ein Strich je
              Spur. Pro Spur gezeichnet bricht er an jeder Zeilengrenze ab und
              sieht aus wie eine Reihe Striche statt wie ein Datum, das für alle
              gilt. Die Namensspalte bleibt frei: der Termin gehört der Zeit,
              nicht den Menschen. */}
          <div className="relative">
            <div className="space-y-2">
            {lanes.map((lane) => {
              const laneKey = lane.personId ?? "open";
              const collapsed = collapsedLanes.includes(laneKey);
              const barCount = lane.groups.reduce(
                (sum, group) => sum + (group.packageBar ? 1 : 0) + group.rows.reduce((n, row) => n + row.length, 0),
                0,
              );
              return (
              // Kein items-start auf der Spur: die klebende Namensspalte wäre
              // dann nur so hoch wie der Name, und unter ihm scrollte der Plan
              // ungedeckt vorbei. Mit der Vorgabe (stretch) deckt die Spalte die
              // ganze Spur ab und der Name richtet sich INNEN oben aus.
              <div key={laneKey} className="flex">
                <div
                  // Schatten nach rechts, nicht nur eine Haarlinie: unter der
                  // klebenden Spalte scrollt beim Blättern der Plan durch, und
                  // ohne sichtbare Kante sieht ein halb verdeckter Balkentitel
                  // nach einem Darstellungsfehler aus statt nach "das geht
                  // darunter weiter".
                  className={`${NAME_COL} sticky left-0 z-[var(--z-sticky)] flex shrink-0 items-start gap-1 border-r border-ink/10 bg-surface pr-3 pt-[0.6rem] shadow-[6px_0_6px_-6px_rgba(0,0,0,0.18)]`}
                >
                  {/* Zuklappen gehört an jede Spur, nicht nur an die
                      unzugewiesene: wer ein Projekt mit zwölf Paketen plant,
                      bekommt zwölf Zeilen, sobald sie sich zeitlich
                      überschneiden - und das ist bei Paketen der Normalfall,
                      nicht die Ausnahme. Zugeklappt bleibt die Spur als Zeile
                      stehen und sagt, wie viel sie verbirgt. */}
                  {barCount > 0 ? (
                    <button
                      type="button"
                      onClick={() =>
                        setCollapsedLanes((cur) =>
                          collapsed ? cur.filter((k) => k !== laneKey) : [...cur, laneKey],
                        )
                      }
                      aria-expanded={!collapsed}
                      aria-label={intl.formatMessage(
                        collapsed
                          ? { id: "project.lanes.expandLane", defaultMessage: "Spur aufklappen" }
                          : { id: "project.lanes.collapseLane", defaultMessage: "Spur zuklappen" },
                      )}
                      className="-ml-1 flex h-6 w-5 shrink-0 items-center justify-center rounded text-smycles-body leading-none text-ink-muted transition hover:text-ink focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ink-ui"
                    >
                      {collapsed ? "▸" : "▾"}
                    </button>
                  ) : null}
                  {/* Wo eine Person steht, steht auch ihr Bild und der Weg zum Profil. */}
                  {lane.personId && personById.has(lane.personId) ? (
                    <PersonLink person={personById.get(lane.personId)!} size="sm" className="smycles-field-value min-w-0" />
                  ) : (
                    <span className="smycles-field-value min-w-0 truncate text-ink-muted">{lane.name}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1 space-y-2">
                  {collapsed ? (
                    <div className="flex h-8 items-center rounded-sm bg-page px-3 ring-1 ring-inset ring-ink/10">
                      <span className="smycles-field-meta text-ink-muted">
                        <FormattedMessage
                          id="project.lanes.collapsedCount"
                          defaultMessage="{count, plural, one {1 Eintrag} other {# Einträge}}"
                          values={{ count: barCount }}
                        />
                      </span>
                    </div>
                  ) : null}
                  {/* Die Spur clippt: die Beschriftung eines schmalen Balkens darf
                      nach rechts in die freie Spur laufen, aber nicht aus ihr heraus
                      und schon gar nicht über die klebende Namensspalte. */}
                  {/* Ein Paket UMRAHMT seine Aufgaben: sie liegen darin, also
                      liegen sie auch in der Zeichnung darin. Der Rahmen ersetzt
                      die Einrückung, die eine Zeitachse nicht haben kann - nach
                      rechts bedeutet dort später, nicht tiefer. */}
                  {(collapsed ? [] : lane.groups).map((group) => {
                    const pkgBar = group.packageBar;
                    const pkgPx = pkgBar ? (pkgBar.widthPercent / 100) * axisWidth : 0;
                    const pkgSelected = pkgBar?.taskId === selectedTaskId;
                    return (
                    <div key={group.packageId} className="relative">
                      {/* DIE getönte Fläche IST das Aufgabenpaket: sie beginnt und
                          endet, wo es beginnt und endet, und die Aufgaben liegen
                          darin. Ein Rahmen über die volle Achse behauptete, das
                          Paket dauere bis zum Rand - die eigene Zeile darunter
                          sagte dann etwas anderes, und zwei Aussagen über
                          dieselbe Dauer sind eine zu viel. */}
                      {pkgBar ? (
                        <button
                          id={`project-lanepkg-${laneKey}-${group.packageId}`}
                          type="button"
                          {...(onSelectTask ? { onClick: () => onSelectTask(pkgBar.taskId) } : {})}
                          title={pkgBar.title}
                          className={`absolute inset-y-0 rounded-md bg-ink/[0.07] text-left ring-1 transition ${
                            pkgSelected ? "smycles-selected ring-2 ring-accent-decoration" : "ring-ink/20 hover:ring-ink/40"
                          }`}
                          style={{
                            left: `${pkgBar.leftPercent}%`,
                            width: `${pkgBar.widthPercent}%`,
                            minWidth: `${MIN_BAR_PX}px`,
                          }}
                        >
                          {/* Passt der Name nicht in die Fläche, steht er rechts
                              daneben - dieselbe Regel wie bei den Balken. */}
                          {/* Innen abgeschnitten, aussen gedeckelt: ein Paketname
                              ist ein freier Text, und "Migration Bestandssystem
                              Phase 2" legte sich sonst über die nächsten drei
                              Wochen. */}
                          <span
                            className={`absolute top-1 max-w-[12rem] truncate text-smycles-label leading-none text-ink ${
                              pkgPx >= LABEL_FITS_PX ? "left-2 right-2" : "left-full ml-1.5"
                            }`}
                          >
                            {pkgBar.title}
                          </span>
                        </button>
                      ) : null}

                      {/* pt-6 lässt die obere Kante der Fläche für den Namen frei.
                          pointer-events-none, damit ein Klick zwischen den Balken
                          bei der Paketfläche darunter ankommt - sonst liegt der
                          Zeilencontainer über ihr und sie ist nirgends greifbar.
                          Die Balken holen sich die Klicks per pointer-events-auto
                          zurück. */}
                      <div className="pointer-events-none relative space-y-0.5 p-1.5 pt-6">
                      {group.rows.map((row, rowIndex) => (
                    <div
                      key={rowIndex}
                      // Kein eigener Kasten je Zeile mehr: eine weisse Fläche mit
                      // Kontur über die ganze Breite las sich wie ein Balken, der
                      // von Anfang bis Ende dauert - und stand damit im
                      // Widerspruch zu dem kurzen Balken, der wirklich darin lag.
                      // Die Zeile ist der GRUND, auf dem Balken liegen; sichtbar
                      // machen sie die Wochen- und Monatslinien.
                      className="relative h-8 overflow-hidden rounded-sm"
                    >
                      {/* Wochen leiser als Monate: Hilfslinien, die beim Abzaehlen
                          helfen, aber nichts behaupten. Auffaellig bleiben allein
                          die Termine. */}
                      {weeks.map((w) => (
                        <div
                          key={`wl-${w.startMs}`}
                          aria-hidden="true"
                          className="absolute inset-y-0 w-px bg-ink/[0.035]"
                          style={{ left: `${w.percent}%` }}
                        />
                      ))}
                      {ticks.map((t) => (
                        <div
                          key={t.ms}
                          className="absolute inset-y-0 w-px bg-ink/[0.09]"
                          style={{ left: `${t.percent}%` }}
                        />
                      ))}
                      {row.map((bar) => {
                        const tone = PACKAGE_TONES[bar.packageIndex % PACKAGE_TONES.length]!;
                        const selected = bar.taskId === selectedTaskId;
                        const clickable = Boolean(onSelectTask);
                        const Tag = clickable ? "button" : "div";
                        const barPx = (bar.widthPercent / 100) * axisWidth;
                        const labelInside = barPx >= LABEL_FITS_PX;
                        return (
                          <Tag
                            key={bar.taskId}
                            id={`project-lanebar-${laneKey}-${bar.taskId}`}
                            {...(clickable
                              ? { type: "button" as const, onClick: () => onSelectTask!(bar.taskId) }
                              : {})}
                            // Der Auswahl-Ring liegt AUSSEN, nicht inset: auf einem
                            // dunklen Aufgabenbalken verschwand er sonst in der
                            // Füllung, und ausgewählt sah aus wie nicht
                            // ausgewählt. Das Paket zeigt es seit jeher so.
                            className={`pointer-events-auto absolute flex items-center overflow-visible rounded-sm text-left ${
                              selected
                                ? "smycles-selected ring-2 ring-accent-decoration"
                                : bar.kind === "package"
                                  ? ""
                                  : "ring-1 ring-inset"
                            } ${
                              labelInside ? "px-2" : ""
                            } ${
                              // Das PAKET trägt keine eigene Kontur mehr - der
                              // Rahmen um die Gruppe ist das Paket, und eine
                              // zweite Umrandung darin sagte dasselbe ein zweites
                              // Mal. Geblieben ist seine LÄNGE als getönte Fläche:
                              // sie ist die einzige Aussage, die der Rahmen nicht
                              // machen kann, weil der immer über die volle Breite
                              // geht.
                              bar.kind === "package"
                                ? "inset-y-2 bg-ink/[0.12]"
                                : `inset-y-1 ${tone.bar}`
                            } ${clickable ? "transition hover:opacity-80" : ""}`}
                            style={{
                              left: `${bar.leftPercent}%`,
                              width: `${bar.widthPercent}%`,
                              minWidth: `${MIN_BAR_PX}px`,
                            }}
                            // Sagt, wozu der Balken gehört. Die Legende darunter
                            // ordnete Ton zu Paket zu, aber es gibt nur vier Töne:
                            // ab dem fünften Paket log sie.
                            //
                            // Paket zuerst, dann die Aufgabe: das ist die
                            // Leserichtung der Zugehörigkeit. Eine Aufgabe kann
                            // in der Spur einer anderen Person liegen als ihr
                            // Paket, dann steht hier gar kein Paketkopf - und
                            // dieser Tooltip ist das Einzige, was sie noch
                            // zuordnet.
                            title={
                              bar.kind === "package"
                                ? bar.title
                                : `${packages[bar.packageIndex]?.name ?? ""} · ${bar.title}`
                            }
                          >
                            {labelInside ? (
                              <span
                                className={`truncate text-smycles-label leading-none ${
                                  bar.kind === "package" ? "text-ink" : tone.text
                                }`}
                              >
                                {bar.title}
                              </span>
                            ) : (
                              /* Daneben statt drin, und ohne die Flaeche zu verbreitern:
                                 der Balken bleibt so lang, wie die Aufgabe dauert. */
                              <span className="pointer-events-none absolute left-full ml-1.5 whitespace-nowrap text-smycles-label leading-none text-ink">
                                {bar.title}
                              </span>
                            )}
                          </Tag>
                        );
                      })}
                    </div>
                  ))}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
              );
            })}
            </div>

          </div>
        </div>
      </div>

    </div>
  );
}
